/* =====================================================================
   MAIN — загрузка, размер canvas (DPR), фикс-таймстеп цикл, ввод.
   Точка входа ESM-сборки (Vite / Electron).
   ===================================================================== */

import '../css/style.css';
import { CONFIG } from './config';
import { RNG, clamp } from './utils';
import { Meta } from './meta';
import { Locale } from './locale';
import { Audio2 } from './audio';
import { Input } from './input';
import { Game } from './game';
import { UI } from './ui';
import { PixiRenderer } from './pixirender';
import { Spawner } from './spawner';
import { Weapons } from './weapons';

let canvas: any, ctx: any, pixiCanvas: any, dpr = 1;

function resize() {
  const cssW = window.innerWidth, cssH = window.innerHeight;
  dpr = Math.min(2, window.devicePixelRatio || 1);
  canvas.width = Math.floor(cssW * dpr);
  canvas.height = Math.floor(cssH * dpr);
  canvas.style.width = cssW + 'px';
  canvas.style.height = cssH + 'px';
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  Game.viewW = cssW;
  Game.viewH = cssH;
  PixiRenderer.resize(cssW, cssH);   // pixi-канвас сам держит свой DPR (resolution)
}

// попадание курсора в прямоугольник (для кликабельных UI-зон)
function hit(r: any) {
  return r && Input.mouseX >= r.x && Input.mouseX <= r.x + r.w &&
         Input.mouseY >= r.y && Input.mouseY <= r.y + r.h;
}

// клик по ряду выбора языка (меню/пауза). true = клик поглощён рядом языка
// (чтобы не провалиться в «клик — начать игру» / кнопки паузы).
function hitLang() {
  if (!Input.clicked || !Game._langRects) return false;
  for (const r of Game._langRects) {
    if (hit(r)) {
      if (Locale.current !== r.lang) { Locale.set(r.lang); Audio2.uiMove(); }
      return true;
    }
  }
  return false;
}

// CRT/ретро-оверлей: класс .crt-on на <body> включает DOM-эффект (см. style.css)
function applyCrt() {
  document.body.classList.toggle('crt-on', Meta.data.crt !== false);
}
function toggleCrt() {
  Meta.data.crt = !(Meta.data.crt !== false);   // флип, undefined трактуем как вкл
  Meta.save();
  applyCrt();
  Audio2.uiMove();
}

function handleInput() {
  const st = Game.state;
  if (st === 'menu') {
    if (Input.wasPressed('m')) Game.openShop();
    else if (Input.clicked && hit(Game._menuSettingsRect)) Game.openSettings('menu');
    else if (Input.clicked && hit(Game._menuShopRect)) Game.openShop();
    else if (Input.wasPressed(' ') || Input.wasPressed('enter')) Game.openCharSelect();
    // клик стартует ТОЛЬКО по верхней зоне старта (не по статистике/языку внизу)
    else if (Input.clicked && hit(Game._menuPlayRect)) Game.openCharSelect();
  } else if (st === 'charselect') {
    const n = Object.keys(CONFIG.characters).length;
    if (Input.wasPressed('arrowleft') || Input.wasPressed('a')) { Game.charIndex = (Game.charIndex + n - 1) % n; Audio2.uiMove(); }
    if (Input.wasPressed('arrowright') || Input.wasPressed('d')) { Game.charIndex = (Game.charIndex + 1) % n; Audio2.uiMove(); }
    // выбор Глубины Тьмы (вверх/вниз), не выше открытой
    const maxD = (typeof Meta !== 'undefined') ? Meta.data.maxDepth : 0;
    if (Input.wasPressed('arrowup') || Input.wasPressed('w')) { Game.selectedDepth = clamp(Game.selectedDepth + 1, 0, maxD); Audio2.uiMove(); }
    if (Input.wasPressed('arrowdown') || Input.wasPressed('s')) { Game.selectedDepth = clamp(Game.selectedDepth - 1, 0, maxD); Audio2.uiMove(); }
    // клик по стрелкам выбора глубины
    let depthClicked = false;
    if (Input.clicked && Game._depthRects) {
      if (hit(Game._depthRects.left)) { Game.selectedDepth = clamp(Game.selectedDepth - 1, 0, maxD); Audio2.uiMove(); depthClicked = true; }
      else if (hit(Game._depthRects.right)) { Game.selectedDepth = clamp(Game.selectedDepth + 1, 0, maxD); Audio2.uiMove(); depthClicked = true; }
    }
    // выбор ПУТИ (стартовый архетип, мета-как-способы) — Q/E или клик-стрелки
    const nPath = CONFIG.paths.length;
    if (Input.wasPressed('q')) { Game.selectedPath = (Game.selectedPath + nPath - 1) % nPath; Audio2.uiMove(); }
    if (Input.wasPressed('e')) { Game.selectedPath = (Game.selectedPath + 1) % nPath; Audio2.uiMove(); }
    let pathClicked = false;
    if (Input.clicked && Game._pathRects) {
      if (hit(Game._pathRects.left)) { Game.selectedPath = (Game.selectedPath + nPath - 1) % nPath; Audio2.uiMove(); pathClicked = true; }
      else if (hit(Game._pathRects.right)) { Game.selectedPath = (Game.selectedPath + 1) % nPath; Audio2.uiMove(); pathClicked = true; }
    }
    if (Game._charRects) {
      for (let i = 0; i < Game._charRects.length; i++) {
        const r = Game._charRects[i];
        if (Input.mouseX >= r.x && Input.mouseX <= r.x + r.w && Input.mouseY >= r.y && Input.mouseY <= r.y + r.h) {
          if (Game.charIndex !== i) { Game.charIndex = i; Audio2.uiMove(); }
        }
      }
    }
    if (Input.wasPressed('enter') || Input.wasPressed(' ')) Game.confirmChar();
    else if (Input.clicked && !depthClicked && !pathClicked && Game._charRects) {
      for (let i = 0; i < Game._charRects.length; i++) {
        const r = Game._charRects[i];
        if (Input.mouseX >= r.x && Input.mouseX <= r.x + r.w && Input.mouseY >= r.y && Input.mouseY <= r.y + r.h) {
          Game.charIndex = i; Game.confirmChar(); break;
        }
      }
    }
  } else if (st === 'playing') {
    if (Input.wasPressed('escape') || Input.wasPressed('p')) Game.togglePause();
  } else if (st === 'speak') {
    // говорящая фигура: клик/пробел/enter — пропустить раньше (иначе авто-уход по таймеру)
    if (Input.wasPressed(' ') || Input.wasPressed('enter') || Input.clicked) Game.dismissSpeak();
  } else if (st === 'paused') {
    if (Input.wasPressed('escape') || Input.wasPressed('p')) Game.togglePause();
    else if (Input.wasPressed('q')) Game.quitToMenu();
    else if (Input.wasPressed('v') && Game.reachedDawn) Game.finishVictory();
    else if (Input.clicked) {
      if (hit(Game._pauseRects && Game._pauseRects.settings)) Game.openSettings('paused');
      else if (hit(Game._pauseRects && Game._pauseRects.victory)) Game.finishVictory();
      else if (hit(Game._pauseRects && Game._pauseRects.resume)) Game.togglePause();
      else if (hit(Game._pauseRects && Game._pauseRects.quit)) Game.quitToMenu();
    }
  } else if (st === 'settings') {
    if (Input.wasPressed('escape') || Input.wasPressed('backspace')) Game.closeSettings();
    else if (Input.clicked) {
      if (hitLang()) { /* язык переключён */ }
      else if (hit(Game._settingsRects && Game._settingsRects.back)) Game.closeSettings();
      else if (hit(Game._settingsRects && Game._settingsRects.crt)) toggleCrt();
      else if (hit(Game._settingsRects && Game._settingsRects.volPlus)) Audio2.setVolume(Audio2.volume + 0.1);
      else if (hit(Game._settingsRects && Game._settingsRects.volMinus)) Audio2.setVolume(Audio2.volume - 0.1);
      else if (hit(Game._settingsRects && Game._settingsRects.volBar)) { const tr = Game._settingsRects.volTrack; Audio2.setVolume((Input.mouseX - tr.x) / tr.w); }
    }
  } else if (st === 'levelup' || st === 'shrine') {
    const n = Game.offers.length;
    if (Input.wasPressed('arrowleft') || Input.wasPressed('a')) { Game.selIndex = (Game.selIndex + n - 1) % n; Audio2.uiMove(); }
    if (Input.wasPressed('arrowright') || Input.wasPressed('d')) { Game.selIndex = (Game.selIndex + 1) % n; Audio2.uiMove(); }
    if (Game._cardRects) {
      for (let i = 0; i < Game._cardRects.length; i++) {
        const r = Game._cardRects[i];
        if (Input.mouseX >= r.x && Input.mouseX <= r.x + r.w && Input.mouseY >= r.y && Input.mouseY <= r.y + r.h) {
          if (Game.selIndex !== i) { Game.selIndex = i; Audio2.uiMove(); }
        }
      }
    }
    Game._rerollHover = !!(Game._rerollRect && hit(Game._rerollRect));
    if (Input.wasPressed('r')) Game.rerollOffers();
    else if (Input.wasPressed('1')) Game.chooseOffer(0);
    else if (Input.wasPressed('2')) Game.chooseOffer(1);
    else if (Input.wasPressed('3')) Game.chooseOffer(2);
    else if (Input.wasPressed('enter') || Input.wasPressed(' ')) Game.chooseOffer(Game.selIndex);
    else if (Input.clicked && Game._rerollRect && hit(Game._rerollRect)) Game.rerollOffers();
    else if (Input.clicked && Game._cardRects) {
      for (let i = 0; i < Game._cardRects.length; i++) {
        const r = Game._cardRects[i];
        if (Input.mouseX >= r.x && Input.mouseX <= r.x + r.w && Input.mouseY >= r.y && Input.mouseY <= r.y + r.h) {
          Game.chooseOffer(i); break;
        }
      }
    }
  } else if (st === 'gameover' || st === 'win') {
    if (Input.wasPressed('m')) Game.state = 'menu';                 // в меню (магазин/смена героя)
    else if (Input.clicked && hit(Game._resultMenuRect)) Game.state = 'menu';
    else if (Input.wasPressed(' ') || Input.clicked) Game.start();  // быстрый рестарт тем же героем
  } else if (st === 'shop') {
    // КОЛОНОЧНАЯ навигация (2 трека): up/down — внутри своей колонки, left/right — переключают колонку.
    // Колонки выводятся из поля track (forge — левая, hearth — правая), в порядке CONFIG.shop.
    const forge = CONFIG.shop.filter((s: any) => s.track !== 'hearth');
    const hearth = CONFIG.shop.filter((s: any) => s.track === 'hearth');
    const fN = forge.length, hN = hearth.length, i = Game.shopIndex;
    const inHearth = i >= fN, col = inHearth ? hearth : forge, colN = inHearth ? hN : fN;
    let row = inHearth ? i - fN : i;
    const toIndex = (h: boolean, r: number) => h ? fN + Math.min(r, hN - 1) : Math.min(r, fN - 1);
    if (Input.wasPressed('arrowup') || Input.wasPressed('w'))   { row = (row + colN - 1) % colN; Game.shopIndex = inHearth ? fN + row : row; Audio2.uiMove(); }
    if (Input.wasPressed('arrowdown') || Input.wasPressed('s')) { row = (row + 1) % colN; Game.shopIndex = inHearth ? fN + row : row; Audio2.uiMove(); }
    if ((Input.wasPressed('arrowleft') || Input.wasPressed('a')) && inHearth && hN)  { Game.shopIndex = toIndex(false, row); Audio2.uiMove(); }
    if ((Input.wasPressed('arrowright') || Input.wasPressed('d')) && !inHearth && hN) { Game.shopIndex = toIndex(true, row); Audio2.uiMove(); }
    if (Game._shopRects) {
      for (let i = 0; i < Game._shopRects.length; i++) {
        const r = Game._shopRects[i];
        if (Input.mouseX >= r.x && Input.mouseX <= r.x + r.w && Input.mouseY >= r.y && Input.mouseY <= r.y + r.h) {
          if (Game.shopIndex !== i) { Game.shopIndex = i; Audio2.uiMove(); }
        }
      }
    }
    if (Input.clicked && hit(Game._shopBackRect)) {
      Game.state = 'menu';
    } else if (Input.wasPressed('enter') || Input.wasPressed(' ') || Input.clicked) {
      if (Meta.buy(CONFIG.shop[Game.shopIndex].key)) Audio2.uiPick(); else Audio2.hit();
    }
    if (Input.wasPressed('escape') || Input.wasPressed('m')) Game.state = 'menu';
  }
}

let acc = 0, last = 0;
const STEP = 1 / 60;

function frame(now: number) {
  if (!last) last = now;
  const dt = Math.min(0.05, (now - last) / 1000);
  last = now;
  Game.clock += dt;

  // отладочная заморозка: рендерим кадр, но не обновляем мир и не читаем ввод
  if ((window as any).__freeze) { UI.render(ctx); PixiRenderer.render(); Input.endFrame(); requestAnimationFrame(frame); return; }

  handleInput();

  acc += dt;
  let steps = 0;
  while (acc >= STEP && steps < 5) { Game.update(STEP); acc -= STEP; steps++; }
  if (acc > STEP * 5) acc = 0;

  // UI.render — ПЕРВЫМ: считает Game.shakeX/Y (seeded rand, детерминизм) и рисует 2D-HUD;
  // PixiRenderer.render — следом: читает shake, рисует мир+тьму на нижнем канвасе.
  UI.render(ctx);
  PixiRenderer.render();
  Input.endFrame();
  requestAnimationFrame(frame);
}

window.addEventListener('load', async () => {
  canvas = document.getElementById('game');
  ctx = canvas.getContext('2d');
  pixiCanvas = document.getElementById('game-pixi');
  // сид RNG: ?seed=N для детерминированных тестов, иначе по времени
  const params = new URLSearchParams(location.search);
  const sp = params.get('seed');
  const seed = sp !== null ? (parseInt(sp, 10) >>> 0) : (Date.now() >>> 0);
  RNG.seed(seed);
  (window as any).__seed = seed;
  Game.clock = 0;
  Meta.load();
  applyCrt();                     // ретро-оверлей по сохранённому флагу (дефолт вкл)
  Locale.init(Meta.data.lang);   // язык игрока, иначе детект браузера (фолбэк en)
  Audio2.volume = (Meta.data.volume != null) ? Meta.data.volume : 0.7;
  Audio2._lastVol = Audio2.volume || 0.7;
  Game.init();
  Input.init(canvas);
  // pixi-рендер мира (WebGL): инициализируем ДО первого кадра
  const cssW = window.innerWidth, cssH = window.innerHeight;
  dpr = Math.min(2, window.devicePixelRatio || 1);
  await PixiRenderer.init(pixiCanvas, cssW, cssH, dpr);
  resize();
  window.addEventListener('resize', resize);
  setupImmersive();
  // ГОРНИЛО: canvas-текст не отрендерит Oswald, пока шрифт не загружен → грузим ключевые
  // начертания до первого кадра (без блокировки навсегда — таймаут на случай offline-сбоя).
  try {
    await Promise.race([
      Promise.all([
        (document as any).fonts.load('700 80px Oswald'),
        (document as any).fonts.load('500 20px Oswald'),
        (document as any).fonts.load('300 14px Oswald'),
        (document as any).fonts.load('200 16px Oswald'),
      ]),
      new Promise(r => setTimeout(r, 1500)),
    ]);
  } catch { /* offline / нет шрифта — fallback в CSS-стеке */ }
  // отладочные хуки для автотестов и плейтест-бота (визуальный режим + ручные читы:
  // SPAWNER/WEAPONS позволяют из page.evaluate быстро выставить ситуацию — спавнить босса/толпу,
  // выдать билд — и заскриншотить. См. tests/playtest-visual.mjs и заметку в AGENTS.md.)
  (window as any).GAME = Game;
  (window as any).RNG = RNG;
  (window as any).INPUT = Input;
  (window as any).META = Meta;
  (window as any).CONFIG = CONFIG;
  (window as any).SPAWNER = Spawner;
  (window as any).WEAPONS = Weapons;
  requestAnimationFrame(frame);
});

// На тач-устройствах по первому жесту: полный экран + блокировка ландшафта.
// Требует пользовательского жеста; на iOS lock недоступен — там работает
// CSS-оверлей #rotate (просьба повернуть). Всё в try/catch — тихо деградирует.
function enterImmersive() {
  const el = document.documentElement as any;
  const fs = el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen || el.msRequestFullscreen;
  const lock = () => {
    try { if ((screen as any).orientation && (screen as any).orientation.lock) (screen as any).orientation.lock('landscape').catch(() => {}); }
    catch (e) {}
  };
  try {
    if (!document.fullscreenElement && fs) {
      const r = fs.call(el);
      if (r && r.then) r.then(lock).catch(lock); else lock();
    } else { lock(); }
  } catch (e) { lock(); }
}

function setupImmersive() {
  const isTouch = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
  if (!isTouch) return;
  const once = () => {
    enterImmersive();
    window.removeEventListener('touchend', once);
    window.removeEventListener('pointerdown', once);
  };
  // первый жест включает иммерсивный режим (один раз)
  window.addEventListener('touchend', once, { passive: true });
  window.addEventListener('pointerdown', once, { passive: true });
  // если игрок вышел из полноэкранного и снова тапнул — попробовать вернуть ландшафт
  document.addEventListener('fullscreenchange', () => {
    if (!document.fullscreenElement) {
      const retry = () => { enterImmersive(); window.removeEventListener('touchend', retry); };
      window.addEventListener('touchend', retry, { passive: true, once: true } as any);
    }
  });
}
